/**
 * Important: it's non-module script, is needed to to use this script without type="module".
 * That's why we can't import any other scripts here, and must be located in entrypoints.
 */

/**
 * @readonly
 * @enum {string}
 */
const CycleStates = {
  init: "init",
  running: "running",
  stopped: "stopped",
};

const PRIORITY_SEPARATOR = 5;
const CYCLE_TIMEOUT = 30;

class EasySchedule {
  constructor() {
    /**
     * @type {CycleStates}
     */
    this.cycleState = CycleStates.init;
    this.mainArray = [];
    this.lateArray = [];
    this.prerequisiteArray = [];
    this.moduleGetters = {
      jquery: () => window.jQuery,
      jqueryui: () => window.jQuery?.Widget,
      c3: () => window.c3,
      ckeditor: () => window.CKEDITOR,
    };
    this.moduleInstances = {};
  }

  startCycle() {
    this.cycleState = CycleStates.running;
    this.cycle();
  }

  sortFunction(a, b) {
    return b.priority - a.priority;
  }

  tick() {
    const countOfProcessedFn = {
      main: 0,
      prerequisite: 0,
    };

    countOfProcessedFn.main = this.executedMain();
    countOfProcessedFn.prerequisite = this.executePrerequisites();

    if (countOfProcessedFn.main === 0 && countOfProcessedFn.prerequisite === 0) {
      this.executedLate();
    }

    if (this.isLoaded()) this.cycleState = CycleStates.stopped;
  }

  isNotNull(a) {
    return a !== null;
  }

  /**
   * Execute all functions in mainArray and return count of them
   * @returns {number}
   */
  executedMain() {
    if (!this.mainArray?.length) return 0;
    const countOfMain = this.mainArray.length;
    this.mainArray.sort(this.sortFunction);
    this.mainArray.forEach(item => item.func());
    this.mainArray = [];
    return countOfMain;
  }

  /**
   * Execute functions in lateArray with higher priority ( > highest - PRIORITY_SEPARATOR)
   */
  executedLate() {
    if (!this.lateArray?.length) return;
    this.lateArray.sort(this.sortFunction);
    const limitPriority = this.lateArray[0].priority - PRIORITY_SEPARATOR;
    const index = this.lateArray.findIndex(item => item.priority <= limitPriority);
    const queue = index < 0 ? [...this.lateArray] : this.lateArray.slice(0, index);
    this.lateArray = index < 0 ? [] : this.lateArray.slice(index);
    queue.forEach(item => {
      item.func.call(window);
    });
  }

  executePrerequisites() {
    if (this.prerequisiteArray.length === 0) return 0;

    let count = 0;
    this.prerequisiteArray = this.prerequisiteArray
      .map(pack => {
        if (this.runFunctionWithPrerequisite(pack?.func, pack?.prerequisite)) {
          count++;
          return null;
        }
        return pack;
      })
      .filter(this.isNotNull);

    if (count) {
      count += this.executePrerequisites();
    }

    return count;
  }

  /**
   * Return prerequisite instance or null if not prepared
   * @param getter
   * @returns {*|null}
   */
  preparePrerequisiteInstance(getter) {
    if (typeof getter !== "string") return getter() || null;
    if (this.moduleInstances[getter]) return this.moduleInstances[getter];
    if (!this.moduleGetters[getter]) return null;
    const instance = this.moduleGetters[getter]();
    if (instance) this.moduleInstances[getter] = instance;
    return instance || null;
  }

  /**
   * Return prerequisite instance or null if not prepared
   * @param getter
   * @returns {*|null}
   */
  preparePrerequisitesInstances(getters) {
    const instances = getters.map(this.preparePrerequisiteInstance.bind(this));
    if (instances.includes(null)) return null;
    return instances;
  }

  cycle() {
    if (!(this.cycleState === CycleStates.running)) return;
    setTimeout(this.cycle.bind(this), CYCLE_TIMEOUT);
    this.tick();
  }

  main(func, priority = 0) {
    if (this.cycleState === CycleStates.init) {
      this.mainArray.push({ func, priority });
    } else {
      func();
    }
  }

  require(func, ...prerequisites) {
    let formattedPrerequisites = prerequisites.map(prerequisite =>
      typeof prerequisite === "string" ? prerequisite.toLowerCase() : prerequisite
    );

    if (!this.runFunctionWithPrerequisite(func, formattedPrerequisites)) {
      this.prerequisiteArray.push({ func, prerequisite: formattedPrerequisites });
    }

    this.runCycleIfNeeded();
  }

  /**
   * Try to run function with prerequisites, if success return true, else false
   * @param func
   * @param prerequisite
   * @returns {boolean}
   */
  runFunctionWithPrerequisite(func, prerequisite) {
    if (!func || !prerequisite || !prerequisite.length) return false;
    let instances = this.preparePrerequisitesInstances(prerequisite);
    if (instances) {
      func.apply(window, instances);
      return true;
    }
    return false;
  }

  runCycleIfNeeded() {
    if (!this.isLoaded() && this.cycleState === CycleStates.stopped) {
      this.cycleState = CycleStates.running;
      this.cycle();
    }
  }

  late(func, priority = 0) {
    if (this.cycleState === CycleStates.init) {
      this.lateArray.push({ func, priority });
    } else {
      func();
    }
  }

  define(name, getter, ...prerequisites) {
    if (prerequisites.length) {
      this.require(() => {
        this.moduleGetters[name.toLowerCase()] = getter;
      }, ...prerequisites);
    } else {
      this.moduleGetters[name.toLowerCase()] = getter;
    }
  }

  isLoaded() {
    return this.mainArray.length <= 0 && this.prerequisiteArray.length <= 0 && this.lateArray.length <= 0;
  }
}

const schedule = new EasySchedule();

window.EASY ||= {};
window.EasyGem ||= {};

window.EASY.schedule = schedule;
window.EasyGem.schedule = schedule;
